import React, { useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import { listPendingProfessionals, approveProfessional, updateUserSubscription } from "../../lib/db";
import { ShieldCheck, Mail, Calendar, UserCheck, ExternalLink, Lock, Unlock, Clock, Sliders } from "lucide-react";
import { 
  Avatar, 
  AvatarImage, 
  AvatarFallback 
} from "../../components/ui/avatar";
import { formatDate } from "../../lib/utils";

export default function AdminDashboard() {
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [activeTab, setActiveTab] = useState("pending");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Buscar Pendentes
      const pendingData = await listPendingProfessionals();
      setPending(pendingData || []);
      
      // 2. Buscar Aprovados com as novas colunas
      const { data: approvedData, error: dbError } = await supabase
        .from("profiles")
        .select("id, full_name, email, role, is_approved, is_blocked, subscription_expires_at, created_at, custom_max_professionals, custom_max_secretaries")
        .eq("is_approved", true)
        .order("full_name");
      
      if (dbError) throw dbError;
      setApproved(approvedData || []);
      
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleApprove = async (id) => {
    if (!window.confirm("Deseja aprovar este profissional?")) return;
    setBusyId(id);
    try {
      // Ao aprovar, já damos os primeiros 30 dias de acesso
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30);

      await approveProfessional(id);
      await updateUserSubscription(id, { 
        subscription_expires_at: expiresAt.toISOString() 
      });
      await load();
    } catch (e) {
      alert("Erro ao aprovar: " + e.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleBlock = async (prof) => {
    const action = prof.is_blocked ? "desbloquear" : "bloquear";
    if (!window.confirm(`Deseja ${action} o acesso de ${prof.full_name}?`)) return;
    
    setBusyId(prof.id);
    try {
      await updateUserSubscription(prof.id, { is_blocked: !prof.is_blocked });
      await load();
    } catch (e) {
      alert("Erro: " + e.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleExtendAccess = async (id) => {
    if (!window.confirm("Deseja renovar o acesso por mais 30 dias?")) return;
    
    setBusyId(id);
    try {
      const { data: current } = await supabase.from("profiles").select("subscription_expires_at").eq("id", id).single();
      const baseDate = current?.subscription_expires_at ? new Date(current.subscription_expires_at) : new Date();
      
      // Se já venceu, começa de hoje. Se não, adiciona ao que já tem.
      const newDate = baseDate < new Date() ? new Date() : baseDate;
      newDate.setDate(newDate.getDate() + 30);

      await updateUserSubscription(id, { subscription_expires_at: newDate.toISOString() });
      await load();
    } catch (e) {
      alert("Erro: " + e.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleEditLimits = async (prof) => {
    const currentLimit = prof.custom_max_professionals || "";
    const promptVal = window.prompt(`Definir limite customizado de PROFISSIONAIS para ${prof.full_name}:\n(Deixe vazio para usar o padrão do plano)`, currentLimit);
    if (promptVal === null) return; // Cancelado
    
    const maxPros = promptVal.trim() === "" ? null : Number(promptVal);
    if (maxPros !== null && (isNaN(maxPros) || maxPros < 1)) {
      alert("Por favor, insira um número válido maior ou igual a 1.");
      return;
    }

    const currentSecLimit = prof.custom_max_secretaries !== null && prof.custom_max_secretaries !== undefined ? prof.custom_max_secretaries : "";
    const promptSecVal = window.prompt(`Definir limite customizado de SECRETÁRIAS para ${prof.full_name}:\n(Deixe vazio para usar o padrão do plano)`, currentSecLimit);
    if (promptSecVal === null) return; // Cancelado
    
    const maxSecs = promptSecVal.trim() === "" ? null : Number(promptSecVal);
    if (maxSecs !== null && (isNaN(maxSecs) || maxSecs < 0)) {
      alert("Por favor, insira um número válido maior ou igual a 0.");
      return;
    }

    setBusyId(prof.id);
    try {
      await updateUserSubscription(prof.id, { 
        custom_max_professionals: maxPros,
        custom_max_secretaries: maxSecs
      });
      await load();
      alert("Limites customizados atualizados com sucesso!");
    } catch (e) {
      alert("Erro ao atualizar limites: " + e.message);
    } finally {
      setBusyId(null);
    }
  };

  const getStatusInfo = (prof) => {
    if (prof.is_blocked) return { label: "Bloqueado", color: "bg-red-500/10 text-red-600" };
    
    const expires = prof.subscription_expires_at ? new Date(prof.subscription_expires_at) : null;
    if (expires && expires < new Date()) return { label: "Vencido", color: "bg-amber-500/10 text-amber-600" };
    
    return { label: "Ativo", color: "bg-emerald-500/10 text-emerald-600" };
  };

  const totalProfessionals = pending.length + approved.length;

  return (
    <div className="ds-fade-up">
      <div className="mb-8">
        <div className="flex items-center gap-2 text-brand font-bold text-xs uppercase tracking-widest mb-2">
          <ShieldCheck className="w-4 h-4" />
          Painel Administrativo
        </div>
        <h1 className="text-4xl font-black tracking-tight text-[hsl(var(--text-primary))]" style={{fontFamily:"'Outfit',sans-serif"}}>
          Gestão da Plataforma
        </h1>
        <p className="text-[hsl(var(--text-secondary))] mt-2 max-w-2xl">
          Aprove novos cadastros e gerencie assinaturas e acessos.
        </p>
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">
          {error}
        </div>
      )}

      {/* Métricas */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="ds-card p-6 border-brand/20 bg-brand/5">
          <div className="text-xs font-bold text-brand uppercase tracking-tighter mb-1">Total de Usuários</div>
          <div className="text-2xl font-black text-[hsl(var(--text-primary))]" style={{fontFamily:"'Outfit',sans-serif"}}>{totalProfessionals}</div>
        </div>
        <div className="ds-card p-6 border-amber-500/20 bg-amber-500/5">
          <div className="text-xs font-bold text-amber-600 uppercase tracking-tighter mb-1">Pendentes</div>
          <div className="text-2xl font-black text-[hsl(var(--text-primary))]" style={{fontFamily:"'Outfit',sans-serif"}}>{pending.length}</div>
        </div>
        <div className="ds-card p-6 border-emerald-500/20 bg-emerald-500/5">
          <div className="text-xs font-bold text-emerald-600 uppercase tracking-tighter mb-1">Ativos</div>
          <div className="text-2xl font-black text-[hsl(var(--text-primary))]" style={{fontFamily:"'Outfit',sans-serif"}}>{approved.filter(p => !p.is_blocked).length}</div>
        </div>
      </div>

      <div className="mt-8">
        <div className="flex gap-4 mb-4 border-b border-[hsl(var(--border-divider))]">
          <button onClick={() => setActiveTab("pending")} className={`pb-2 px-1 text-sm font-bold transition-all ${activeTab === "pending" ? "border-b-2 border-brand text-brand" : "text-[hsl(var(--text-tertiary))]"}`}>
            Pendentes ({pending.length})
          </button>
          <button onClick={() => setActiveTab("approved")} className={`pb-2 px-1 text-sm font-bold transition-all ${activeTab === "approved" ? "border-b-2 border-brand text-brand" : "text-[hsl(var(--text-tertiary))]"}`}>
            Gerenciar Ativos ({approved.length})
          </button>
        </div>

        <div className="ds-card overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-sm text-[hsl(var(--text-tertiary))]">Carregando...</div>
          ) : (
            <div className="divide-y divide-[hsl(var(--border-divider))]">
              {(activeTab === "pending" ? pending : approved).length === 0 ? (
                <div className="p-12 text-center text-sm text-[hsl(var(--text-secondary))]">Nada encontrado nesta categoria.</div>
              ) : (
                (activeTab === "pending" ? pending : approved).map((prof) => {
                  const status = getStatusInfo(prof);
                  return (
                    <div key={prof.id} className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:bg-[hsl(var(--bg-hover))]/20 transition-colors">
                      <div className="flex items-center gap-4">
                        <Avatar className="h-12 w-12">
                          <AvatarFallback className="bg-brand/10 text-brand font-bold">
                            {prof.full_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-bold text-[hsl(var(--text-primary))] flex items-center gap-2">
                            {prof.full_name}
                            {prof.role === 'superadmin' && <span className="text-[10px] bg-brand text-white px-1.5 py-0.5 rounded">ADMIN</span>}
                          </div>
                          <div className="text-xs text-[hsl(var(--text-tertiary))]">{prof.email}</div>
                          {prof.subscription_expires_at && (
                            <div className="flex items-center gap-1.5 text-[10px] mt-1 font-bold text-[hsl(var(--text-tertiary))]">
                              <Clock className="w-3 h-3" /> Vence em: {formatDate(prof.subscription_expires_at)}
                            </div>
                          )}
                          {(prof.custom_max_professionals !== null || prof.custom_max_secretaries !== null) && (
                            <div className="text-[10px] font-bold text-emerald-600 mt-1 uppercase tracking-tighter">
                              ★ Limites: {prof.custom_max_professionals ?? "Padrão"} Prós / {prof.custom_max_secretaries ?? "Padrão"} Secs
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${status.color}`}>
                          {status.label}
                        </div>
                        
                        {activeTab === "pending" ? (
                          <button disabled={busyId === prof.id} onClick={() => handleApprove(prof.id)} className="ds-btn-primary h-9 px-4 text-xs">
                            {busyId === prof.id ? "..." : "Aprovar"}
                          </button>
                        ) : (
                          <div className="flex gap-2">
                            <button 
                              disabled={busyId === prof.id}
                              onClick={() => handleEditLimits(prof)}
                              title="Definir limites customizados de equipe"
                              className="p-2 rounded-lg bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 transition-colors"
                            >
                              <Sliders className="w-4 h-4" />
                            </button>
                            <button 
                              disabled={busyId === prof.id}
                              onClick={() => handleExtendAccess(prof.id)}
                              title="Renovar +30 dias"
                              className="p-2 rounded-lg bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 transition-colors"
                            >
                              <Calendar className="w-4 h-4" />
                            </button>
                            <button 
                              disabled={busyId === prof.id}
                              onClick={() => handleToggleBlock(prof)}
                              title={prof.is_blocked ? "Desbloquear" : "Bloquear"}
                              className={`p-2 rounded-lg transition-colors ${prof.is_blocked ? 'bg-amber-500/10 text-amber-600 hover:bg-amber-500/20' : 'bg-red-500/10 text-red-600 hover:bg-red-500/20'}`}
                            >
                              {prof.is_blocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
